import { Alert, Box, Button, Grid, InputAdornment, MenuItem, Paper, Typography, IconButton, Modal, Fade, Backdrop, Divider, Card, CardHeader, CardContent } from "@mui/material";
import { FormHandles } from "@unform/core";
import { VForm, VTextField, VMaskTextField } from "../../../shared/forms";
import React, { useRef, useEffect, useState } from "react";
import { PlanListEditor } from "./PlanListEditor";
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon, Close as CloseIcon, Description as DescriptionIcon } from "@mui/icons-material";
import { globalRemoveMaskReal2 } from "../../../shared/utils";
import { useProfile } from "../../../context/ProfileContext";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";

interface AnalysisEditModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    initialData: any;
    isLoading?: boolean;
    isBlacklist?: number;
}

export const AnalysisEditModal: React.FC<AnalysisEditModalProps> = ({
    open,
    onClose,
    onSubmit,
    initialData,
    isLoading = false,
    isBlacklist = 0,
}) => {
    const formRef = useRef<FormHandles>(null);
    const [showRawJson, setShowRawJson] = useState(false);
    const { permissions, permissionId, isSuperAdmin } = useProfile();

    const canAlterStatus = isSuperAdmin || !!permissions?.[PermissionRolesEnum.AnalysisOptions]?.[PermissionActionsEnum.AlterStatus];
    const canUpdatePlans = isSuperAdmin || !!permissions?.[PermissionRolesEnum.AnalysisOptions]?.[PermissionActionsEnum.UpdatePlans];

    useEffect(() => {
        if (open && initialData) {
            // Aguarda um ciclo para garantir que o form esteja montado
            setTimeout(() => {
                const formattedInitialData = { ...initialData };
                try {
                    let parsedPlanlist: any[] = [];
                    if (formattedInitialData.planlist) {
                        parsedPlanlist = typeof formattedInitialData.planlist === 'string'
                            ? JSON.parse(formattedInitialData.planlist)
                            : formattedInitialData.planlist;
                        formattedInitialData.planlist = JSON.stringify(parsedPlanlist, null, 2);
                    }

                    // Helper to get value from root or first plan
                    const getValue = (key: string, planKey: string) => {
                        const rootValue = formattedInitialData[key];
                        if (rootValue && Number(rootValue) !== 0) return rootValue;

                        if (parsedPlanlist.length > 0) {
                            const firstPlan = parsedPlanlist[0];
                            return firstPlan[planKey] || firstPlan[key] || 0;
                        }
                        return 0;
                    };

                    // Ensure currency fields are strings for the mask and formatted correctly
                    const rentAmount = getValue('rentAmount', 'rent_amount');
                    const condominiumAmount = formattedInitialData.condominiumAmount || 0; // Condo usually not in plan
                    const lmi = getValue('lmi', 'lmi');

                    const waterAmount = formattedInitialData.waterAmount || 0;
                    const iptuAmount = formattedInitialData.iptuAmount || 0;
                    const gasAmount = formattedInitialData.gasAmount || 0;
                    const electricEnergyAmount = formattedInitialData.electricEnergyAmount || 0;

                    formattedInitialData.rentAmount = Number(rentAmount).toFixed(2);
                    formattedInitialData.condominiumAmount = Number(condominiumAmount).toFixed(2);
                    formattedInitialData.lmi = lmi;

                    formattedInitialData.waterAmount = Number(waterAmount).toFixed(2);
                    formattedInitialData.iptuAmount = Number(iptuAmount).toFixed(2);
                    formattedInitialData.gasAmount = Number(gasAmount).toFixed(2);
                    formattedInitialData.electricEnergyAmount = Number(electricEnergyAmount).toFixed(2);

                } catch (e) {
                    console.error("Error formatting initial planlist", e);
                }
                formRef.current?.setData(formattedInitialData);
            }, 100);
        }
    }, [open, initialData]);

    const handleSubmit = async (data: any) => {
        // Ensure planlist is a string
        let formattedPlanlist = initialData.planlist;

        if (permissionId === '1' && canUpdatePlans && data.planlist) {
            try {
                const parsed = typeof data.planlist === 'string' ? JSON.parse(data.planlist) : data.planlist;
                formattedPlanlist = JSON.stringify(parsed);
            } catch (e) {
                console.error("Error minifying planlist", e);
                formattedPlanlist = data.planlist; // Fallback to what was sent if parse fails
            }
        }

        const payload = {
            ...data,
            id: initialData?.id,
            planlist: formattedPlanlist,
            lmi: canAlterStatus ? (data.lmi || "0") : initialData.lmi,
            rentAmount: canAlterStatus ? (globalRemoveMaskReal2(String(data.rentAmount || "")) || "0.00") : initialData.rentAmount,
            condominiumAmount: canAlterStatus ? (globalRemoveMaskReal2(String(data.condominiumAmount || "")) || "0.00") : initialData.condominiumAmount,
            waterAmount: canAlterStatus ? (globalRemoveMaskReal2(String(data.waterAmount || "")) || "0.00") : initialData.waterAmount,
            iptuAmount: canAlterStatus
                ? (
                    String(data.iptuAmount ?? "").trim() !== ""
                        ? (globalRemoveMaskReal2(String(data.iptuAmount)) || "0.00")
                        : (initialData.iptuAmount || "0.00")
                )
                : initialData.iptuAmount,
            gasAmount: canAlterStatus ? (globalRemoveMaskReal2(String(data.gasAmount || "")) || "0.00") : initialData.gasAmount,
            electricEnergyAmount: canAlterStatus ? (globalRemoveMaskReal2(String(data.electricEnergyAmount || "")) || "0.00") : initialData.electricEnergyAmount,
            residentialFee: initialData.residentialFee || "0.00",
            commercialFee: initialData.commercialFee || "0.00",
            fee: initialData.fee || "0.00",
            status: canAlterStatus ? data.status : initialData.status,
            createdFrom: canAlterStatus ? data.createdFrom : initialData.createdFrom,
            obs: canAlterStatus ? data.obs : initialData.obs,
            webhook: canAlterStatus ? data.webhook : initialData.webhook,
        };
        onSubmit(payload);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{ backdrop: { timeout: 500 } }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "900px",
                        maxWidth: "95vw",
                        maxHeight: "90vh",
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 0,
                        display: "flex",
                        flexDirection: "column",
                        outline: "none",
                        overflow: "hidden"
                    }}
                >
                    {/* Header */}
                    <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #e0e0e0", display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#fff" }}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <DescriptionIcon sx={{ fontSize: 30, color: "primary.main" }} />
                            <Typography variant="h5" fontWeight={400} color="primary.main">
                                Alteração de Análise
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <VForm
                        ref={formRef}
                        onSubmit={handleSubmit}
                        placeholder=""
                        onPointerEnterCapture={() => { }}
                        onPointerLeaveCapture={() => { }}
                        style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
                    >
                        {/* Scrollable Content */}
                        <Box sx={{ p: 3, overflowY: "auto", flex: 1, bgcolor: "#f8f9fa" }}>
                            {isBlacklist > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Alert severity="error">Este cliente está bloqueado (BLK100).</Alert>
                                </Box>
                            )}

                            {/* Analysis Data Card */}
                            {canAlterStatus && (
                                <Card elevation={0} sx={{ border: "1px solid #e0e0e0", mb: 3, bgcolor: "#fff", borderRadius: 4, p: 1 }}>
                                    <CardHeader
                                        title="Dados da Análise"
                                        titleTypographyProps={{ variant: "subtitle1", fontWeight: "bold", color: "text.primary" }}
                                        sx={{ pb: 0 }}
                                    />
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <VTextField fullWidth label="Status" name="status" disabled={isLoading} select>
                                                    <MenuItem value="pre_approved" disabled={isBlacklist > 0}>Pré-Aprovada</MenuItem>
                                                    <MenuItem value="awaiting_biometria" disabled={isBlacklist > 0}>Pendente biometria</MenuItem>
                                                    <MenuItem value="awaiting_signature" disabled={isBlacklist > 0}>Assinatura Pendente</MenuItem>
                                                    <MenuItem value="in_hiring" disabled={isBlacklist > 0}>Em contratação</MenuItem>
                                                    <MenuItem value="in_progress" disabled={isBlacklist > 0}>Em Andamento</MenuItem>
                                                    <MenuItem value="issued" disabled={isBlacklist > 0}>Aprovada</MenuItem>
                                                    <MenuItem value="pending" disabled={isBlacklist > 0}>Pendente</MenuItem>
                                                    <MenuItem value="expired">Expirada</MenuItem>
                                                    <MenuItem value="refused">Recusada</MenuItem>
                                                    <MenuItem value="canceled">Cancelada</MenuItem>
                                                    <MenuItem value="hired">Contratada</MenuItem>
                                                </VTextField>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <VTextField fullWidth label="Criado de" name="createdFrom" disabled={isLoading} select>
                                                    <MenuItem value="web_app">Aplicação</MenuItem>
                                                    <MenuItem value="web_service">Integração</MenuItem>
                                                </VTextField>
                                            </Grid>

                                            <Grid item xs={12} md={4}>
                                                <VMaskTextField
                                                    fullWidth
                                                    label="Valor Aluguel"
                                                    name="rentAmount"
                                                    mask="real"
                                                    disabled={isLoading}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <VMaskTextField
                                                    fullWidth
                                                    label="Valor Condomínio"
                                                    name="condominiumAmount"
                                                    mask="real"
                                                    disabled={isLoading}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={4}>
                                                <VMaskTextField
                                                    fullWidth
                                                    label="Valor IPTU"
                                                    name="iptuAmount"
                                                    mask="real"
                                                    disabled={isLoading}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={4}>
                                                <VMaskTextField
                                                    fullWidth
                                                    label="Valor Água"
                                                    name="waterAmount"
                                                    mask="real"
                                                    disabled={isLoading}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <VMaskTextField
                                                    fullWidth
                                                    label="Valor Energia"
                                                    name="electricEnergyAmount"
                                                    mask="real"
                                                    disabled={isLoading}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <VMaskTextField
                                                    fullWidth
                                                    label="Valor Gás"
                                                    name="gasAmount"
                                                    mask="real"
                                                    disabled={isLoading}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <VTextField
                                                    fullWidth
                                                    label="LMI (Multiplicador)"
                                                    name="lmi"
                                                    disabled={isLoading}
                                                    type="number"
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <VTextField fullWidth label="Webhook" name="webhook" disabled={isLoading} />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <VTextField fullWidth label="Observações" name="obs" disabled={isLoading} multiline rows={2} />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Plans Card */}
                            {permissionId === '1' && canUpdatePlans && (
                                <Card elevation={0} sx={{ border: "1px solid #e0e0e0", mb: 3, bgcolor: "#fff", borderRadius: 4, p: 1 }}>
                                    <CardHeader
                                        title="Planos Configurados"
                                        titleTypographyProps={{ variant: "subtitle1", fontWeight: "bold", color: "text.primary" }}
                                        sx={{ pb: 0 }}
                                    />
                                    <CardContent>
                                        <PlanListEditor
                                            value={initialData?.planlist || "[]"}
                                            allowRemove
                                        planRevisionStatus={
                                            initialData?.client?.planRevision ??
                                            initialData?.planRevision ??
                                            initialData?.plan_revision ??
                                            initialData?.planRevisionStatus ??
                                            null
                                        }
                                        revisionPlanId={
                                            (initialData?.client?.planRevision as any)?.planId ??
                                            (initialData?.client?.planRevision as any)?.plan_id ??
                                            (initialData?.client?.planRevision as any)?.id ??
                                            initialData?.planId ??
                                            initialData?.plan_id ??
                                            initialData?.client?.planId ??
                                            initialData?.client?.plan_id ??
                                            initialData?.revisionPlanId ??
                                            initialData?.revision_plan_id ??
                                            null
                                        }
                                            onChange={(newValue) => {
                                                formRef.current?.setFieldValue("planlist", newValue);
                                            }}
                                        />
                                    </CardContent>
                                </Card>
                            )}

                            {permissionId === '1' && canUpdatePlans && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 3, justifyContent: 'flex-end' }}>
                                        <Typography variant="caption" sx={{ color: '#777', mr: 1 }}>
                                            JSON Bruto
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => setShowRawJson(!showRawJson)}
                                        >
                                            {showRawJson ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                        </IconButton>
                                    </Box>

                                    <Box sx={{ display: showRawJson ? 'block' : 'none', mb: 2, backgroundColor: '#FFFF', borderRadius: 2 }}>
                                        <VTextField
                                            name="planlist"
                                            multiline
                                            rows={10}
                                            fullWidth
                                            label="PlanList"
                                            InputProps={{
                                                style: {
                                                    fontFamily: 'monospace',
                                                    backgroundColor: '#1e1e1e',
                                                    color: '#d4d4d4',
                                                    fontSize: '0.875rem'
                                                }
                                            }}
                                            InputLabelProps={{
                                                style: { color: '#888' }
                                            }}
                                        />
                                    </Box>
                                </>
                            )}
                        </Box>

                        {/* Footer Actions */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, bgcolor: "#fff", borderTop: "1px solid #e0e0e0" }}>
                            <Button
                                sx={{ color: "#7924c7", fontWeight: "bold" }}
                                disableElevation
                                variant="text"
                                onClick={onClose}
                            >
                                FECHAR
                            </Button>
                            <Button
                                sx={{

                                    fontWeight: "bold"
                                }}
                                disableElevation
                                variant="contained"
                                type="submit"
                                disabled={isLoading}
                            >
                                SALVAR
                            </Button>
                        </Box>
                    </VForm>
                </Box>
            </Fade>
        </Modal>
    );
};
